import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { DEFAULT_WIF_REQUEST_PARAMETER } from "@p0security/shared/types/agentic/credential";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { parseClusterId } from "./components.ts";
import { REDSHIFT_AI_INTEGRATION_KEY } from "./constants.ts";
import { RedshiftAiIconUrl } from "./icon.ts";
import { redshiftAiMcpTools } from "./mcp-tools.ts";
import type { RedshiftAiIntegration } from "./types.ts";

// copy of imageFromEnv in packages/integrations/gcloud/shared/src/mcp/servers.ts
/** The image `env` names, or `fallback` where it is unset, such as in the
 * browser. */
const imageFromEnv = (args: { env: string; fallback: string }): string =>
  (typeof process === "undefined" ? undefined : process.env[args.env]) ||
  args.fallback;

/** AWS's Redshift MCP server, pinned to the digest V1 checked. */
export const REDSHIFT_MCP_IMAGE =
  "public.ecr.aws/awslabs-mcp/awslabs/redshift-mcp-server:0.1.2@sha256:6b1bcb67a9314bdbed6fab0a5f316bdf41afa4e20481750b55da41815672ccd4";

const redshiftAiPropagationHint = propagationHint({
  resource: "Redshift",
  normalWait: "10 to 30 seconds",
  maxWait: "1 minute",
  pollInterval: "5 seconds",
});

const prefixArgs = ["redshift-ai"];

const accessPrompt = (
  name: string
) => `Request access to the Redshift cluster ${name}. Each grant gets its own
database user, which holds what the grant names. Every request needs a
duration of at most 365 days: one without an end time, or a longer one, is
refused.

Pass the access type, then the argv for it.
`;

const durationArgs = `  - duration: How long access lasts, at most 365 days, as a number and
    one of minutes, hours, days or weeks, e.g. '2 hours', '5 days' or
    '1 week'
  - reason: The reason access is needed`;

const roleField = { description: "Redshift role", required: [] };

const grantField = { description: "PRIVILEGE:OBJECT entry", required: [] };

// copy of snowflakeAiMcp in packages/integrations/snowflake-ai/shared/src/mcp.ts,
// as a container, without the warehouse
/**
 * The MCP server for one installed cluster, by its item id. The cluster is
 * pinned: every request and listing carries it, after the access type, so an
 * agent can only request access to the cluster its server reaches.
 *
 * The gateway runs AWS's server with the grant's IAM role's temporary keys.
 * The region comes from the item id, which is also the cluster's region.
 */
export const redshiftAiMcp = (
  clusterId: string
): McpServerSpec & { type: "container" } => {
  const { name, region } = parseClusterId(clusterId) ?? {
    name: clusterId,
    region: "",
  };
  return {
    type: "container",
    transport: "stdio",
    label: `Amazon Redshift (${name})`,
    iconUrl: RedshiftAiIconUrl,
    credential: {
      type: "wif",
      csp: "aws",
      parameter: DEFAULT_WIF_REQUEST_PARAMETER,
      // The grant's own IAM role, which stage named.
      awsRoleArn: "{{ request.generated.arn }}",
    },
    // The grant's database user decides what Redshift allows.
    authorization: "upstream",
    container: {
      command: [],
      args: [],
      envs: {
        // The gateway's AwsTempCreds field names, not the AWS CLI's.
        AWS_ACCESS_KEY_ID: "{{credential.accessKeyId}}",
        AWS_SECRET_ACCESS_KEY: "{{credential.secretAccessKey}}",
        AWS_SESSION_TOKEN: "{{credential.sessionToken}}",
        AWS_REGION: region,
        ACCESS_MODE: "read-write",
        // The gateway can't relay MCP elicitation, so the server can't ask
        // before a write. P0's approval and the user's grants limit writes.
        UNSAFE_SKIP_WRITE_CONFIRMATION: "true",
      },
      image: {
        image: imageFromEnv({
          env: "REDSHIFT_MCP_IMAGE",
          fallback: REDSHIFT_MCP_IMAGE,
        }),
      },
    },
    tools: { type: "manual", tools: redshiftAiMcpTools },
    location: {
      key: "cluster",
      description: "Redshift cluster or Serverless workgroup",
    },
    access: {
      prompt: accessPrompt(name),
      prefixArgs,
      propagationHint: redshiftAiPropagationHint,
      accessTypes: {
        role: {
          prompt: `For access_type "role", call with argv:

  [<role>..., "--duration", <duration>, "--reason", <reason>]

where:

  - role: One or more existing Redshift roles. Use the list tool to enumerate them.
${durationArgs}
`,
          args: ["role", clusterId],
        },
        objects: {
          prompt: `For access_type "objects", call with argv:

  [<grant>..., "--duration", <duration>, "--reason", <reason>]

where:

  - grant: One or more PRIVILEGE:OBJECT entries. PRIVILEGE is SELECT, INSERT,
    UPDATE or DELETE. OBJECT is a database (DB), a schema (DB.SCHEMA), or a
    table or view (DB.SCHEMA.TABLE); entries may name different databases. A
    database or schema covers the tables and views in it when access is
    granted. Request only what your queries need. Use the list tool to
    enumerate them.
${durationArgs}
`,
          args: ["objects", clusterId],
        },
      },
    },
    list: {
      prefixArgs,
      fields: { name: roleField, grants: grantField },
      accessTypes: {
        role: {
          args: ["role", "--cluster", clusterId],
          fields: { name: roleField },
        },
        objects: {
          args: ["objects", "--cluster", clusterId],
          fields: { grants: grantField },
        },
      },
    },
  };
};

/** One Redshift MCP server per installed cluster, for the agentic catalog's
 * `redshift` family. Its type is `McpServerFamily` in
 * `@p0security/integrations-agentic-shared/mcp/registry`, given by shape,
 * since that package depends on this one.
 *
 * The item id comes from a stored server's id and becomes the region the
 * server calls, so one that isn't a cluster id names no server.
 */
export const redshiftAiMcpServerFamily = {
  items: (installed: Record<string, NewConfig>): string[] =>
    installedIds(
      "cluster",
      (installed[REDSHIFT_AI_INTEGRATION_KEY] ?? {}) as RedshiftAiIntegration
    ),
  spec: (clusterId: string) =>
    parseClusterId(clusterId) ? redshiftAiMcp(clusterId) : undefined,
};
